import { useMemo, useState } from 'react';
import { isoDay } from '../lib/format';
import { C, ink } from '../lib/theme';
import { IconCheck } from './icons';

/**
 * Period Selector (PRD §8): duas abas no mesmo popover.
 *  - "Períodos" (padrão, operacional): Hoje / Esta semana / Este mês / Personalizado
 *  - "Meses" (analítico): checklist dos últimos N meses, pra comparar meses
 *    não-contíguos como o Lovable permite.
 * Multi-seleção de meses vira o range [menor início, maior fim] — é o que o
 * backend aceita (from/to); a UI deixa isso explícito no rótulo.
 */
export function PeriodSelector({ from, to, onApply, onClear }: {
  from?: string; to?: string;
  onApply: (from?: string, to?: string) => void;
  onClear: () => void;
}) {
  const [tab, setTab] = useState<'periodos' | 'meses'>('periodos');
  const [customFrom, setCustomFrom] = useState(from ?? '');
  const [customTo, setCustomTo] = useState(to ?? '');

  const months = useMemo(() => {
    const out: Array<{ key: string; label: string; from: string; to: string }> = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      out.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
        from: isoDay(d), to: isoDay(last),
      });
    }
    return out;
  }, []);

  const [picked, setPicked] = useState<string[]>([]);

  function toggleMonth(key: string) {
    const next = picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key];
    setPicked(next);
    if (next.length === 0) { onClear(); return; }
    const chosen = months.filter((m) => next.includes(m.key));
    const lo = chosen.reduce((a, m) => (m.from < a ? m.from : a), chosen[0].from);
    const hi = chosen.reduce((a, m) => (m.to > a ? m.to : a), chosen[0].to);
    onApply(lo, hi);
  }

  const today = new Date();
  const shortcuts = [
    { label: 'Hoje', from: isoDay(today), to: isoDay(today) },
    {
      label: 'Esta semana',
      from: isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7))),
      to: isoDay(today),
    },
    { label: 'Este mês', from: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDay(today) },
    { label: 'Últimos 90 dias', from: isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89)), to: isoDay(today) },
  ];

  const optStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', cursor: 'pointer',
    font: 'inherit', textAlign: 'left', fontSize: 13, fontWeight: active ? 750 : 600,
    padding: '8px 10px', borderRadius: 9,
    background: active ? ink(0.09) : 'transparent', color: active ? C.fg : ink(0.75),
  });

  const box = (on: boolean) => (
    <span style={{
      width: 14, height: 14, borderRadius: 4, flex: 'none',
      border: `1.5px solid ${on ? C.accent : ink(0.25)}`, background: on ? C.accent : 'transparent',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
    }}>{on ? <IconCheck size={10} /> : null}</span>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, background: ink(0.06), borderRadius: 10, padding: 3, marginBottom: 10 }}>
        {(['periodos', 'meses'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={{
            flex: 1, border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
            padding: '6px 0', borderRadius: 8,
            background: tab === t ? ink(0.13) : 'transparent', color: tab === t ? C.fg : ink(0.5),
          }}>{t === 'periodos' ? 'Períodos' : 'Meses'}</button>
        ))}
      </div>

      {tab === 'periodos' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shortcuts.map((s) => {
            const active = from === s.from && to === s.to;
            return (
              <button key={s.label} type="button" className="ko-opt" style={optStyle(active)}
                onClick={() => { setPicked([]); onApply(s.from, s.to); }}>
                {box(active)}{s.label}
              </button>
            );
          })}
          <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px dashed ${ink(0.14)}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: ink(0.45), marginBottom: 7, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Personalizado</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={dateInput} />
              <span style={{ fontSize: 11, color: ink(0.3) }}>até</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={dateInput} />
            </div>
            <button type="button" onClick={() => { setPicked([]); onApply(customFrom || undefined, customTo || undefined); }}
              style={{
                width: '100%', marginTop: 8, border: 'none', cursor: 'pointer', font: 'inherit',
                fontSize: 12, fontWeight: 700, padding: '7px 0', borderRadius: 9,
                background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
              }}>Aplicar</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflow: 'auto' }}>
          {months.map((m) => (
            <button key={m.key} type="button" className="ko-opt" style={optStyle(picked.includes(m.key))}
              onClick={() => toggleMonth(m.key)}>
              {box(picked.includes(m.key))}
              <span style={{ textTransform: 'capitalize' }}>{m.label}</span>
            </button>
          ))}
        </div>
      )}

      {(from || to) && (
        <button type="button" onClick={() => { setPicked([]); onClear(); }} style={{
          width: '100%', marginTop: 9, border: `1px solid ${ink(0.12)}`, background: 'transparent',
          color: ink(0.5), cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 600,
          padding: '7px 9px', borderRadius: 9,
        }}>Limpar período</button>
      )}
    </div>
  );
}

const dateInput: React.CSSProperties = {
  flex: 1, minWidth: 0, font: 'inherit', fontSize: 11.5, padding: '6px 8px', borderRadius: 8,
  border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)', color: C.fg,
  outline: 'none',
};
